import styled from '@emotion/styled'
import NumberInput from 'components/composite/NumberInput'
import Column from 'components/core/Column'
import { IconButton } from 'components/core/IconButton'
import Row from 'components/core/Row'
import Select from 'components/core/Select'
import TextInput from 'components/core/TextInput'
import Typography from 'components/core/Typography'
import { DESIGN_TOKENS } from 'constants/color.constants'
import { FREIGHT_TYPE_OPTIONS } from 'constants/orderCommodity.constants'
import { SPACING } from 'constants/spacing.constants'
import { Copy, X } from 'lucide-react'
import { MeasurementType, WeightType } from 'types/order.types'
import Block from 'components/core/Block'
import type { ConfirmShipmentDetailsCommodityItem } from 'types/widget.types'
import type { ConfirmShipmentDetailsCommodityErrors } from '../confirmShipmentDetailsWidget.types'

const StyledConfirmShipmentDetailsWidgetCommodity = styled(Column)`
    background-color: ${DESIGN_TOKENS.background.secondary};
    border: 1px solid ${DESIGN_TOKENS.border.input};
    border-radius: ${SPACING.p1};
    padding-top: ${SPACING.p2};
    padding-bottom: ${SPACING.p2};
    padding-left: ${SPACING.p1};
    padding-right: ${SPACING.p1};
`

export const ConfirmShipmentDetailsWidgetCommodity = ({
    isMobile,
    hideDeleteButton,
    isRequiredLabel,
    label,
    commodity,
    commodityErrors,
    measurementType,
    weightType,
    onChange,
    onRemove,
    onDuplicate,
}: {
    isMobile?: boolean
    hideDeleteButton?: boolean
    isRequiredLabel?: boolean
    label: string
    measurementType: MeasurementType
    weightType: WeightType
    commodity: ConfirmShipmentDetailsCommodityItem
    commodityErrors: ConfirmShipmentDetailsCommodityErrors
    onChange: (commodity: ConfirmShipmentDetailsCommodityItem) => void
    onRemove: () => void
    onDuplicate: () => void
}) => {
    if (isMobile) {
        return (
            <StyledConfirmShipmentDetailsWidgetCommodity gap="p2">
                <Row justifyContent="space-between">
                    <Typography isBold variant="body2" color="primary">
                        {label}
                    </Typography>
                    <Row gap="sm">
                        <IconButton size="sm" onClick={onDuplicate}>
                            <Copy size={16} />
                        </IconButton>
                        {!hideDeleteButton && (
                            <IconButton size="sm" onClick={onRemove}>
                                <X size={16} />
                            </IconButton>
                        )}
                    </Row>
                </Row>
                <Row gap="p1">
                    <Block widthPct={70}>
                        <TextInput
                            isRequiredLabel={isRequiredLabel}
                            label="Description"
                            placeholder="Description"
                            value={commodity.description ?? ''}
                            error={commodityErrors.description}
                            onChange={(description) =>
                                onChange({ ...commodity, description })
                            }
                        />
                    </Block>
                    {/* "# of units" field temporarily hidden - may be added back later
                    <Block widthPct={30}>
                        <NumberInput
                            label="# of units"
                            placeholder="# of units"
                            value={commodity.numOfUnits ?? undefined}
                            error={commodityErrors.numOfUnits}
                            onChange={(numOfUnits) =>
                                onChange({ ...commodity, numOfUnits })
                            }
                        />
                    </Block>
                    */}
                </Row>
                <Row gap="p1">
                    <Block widthPct={70}>
                        <Select
                            shouldPortalMenu
                            label="Type"
                            value={commodity.freightType ?? undefined}
                            placeholder="Type"
                            menuPlacement="top"
                            options={FREIGHT_TYPE_OPTIONS}
                            onSelectedOptionChanged={(freightType) =>
                                onChange({
                                    ...commodity,
                                    freightType:
                                        freightType as ConfirmShipmentDetailsCommodityItem['freightType'],
                                })
                            }
                        />
                    </Block>
                    <Block widthPct={30}>
                        <NumberInput
                            isRequiredLabel={isRequiredLabel}
                            label="Qty"
                            placeholder="Qty"
                            value={commodity.quantity ?? undefined}
                            error={commodityErrors.quantity}
                            onChange={(quantity) =>
                                onChange({ ...commodity, quantity })
                            }
                        />
                    </Block>
                </Row>
                <Row gap="p1">
                    <Block widthPct={50}>
                        <NumberInput
                            isRequiredLabel={isRequiredLabel}
                            label="Lg"
                            value={commodity.length ?? undefined}
                            error={commodityErrors.len}
                            placeholder="Lg"
                            suffix={` ${measurementType}`}
                            onChange={(length) =>
                                onChange({ ...commodity, length })
                            }
                        />
                    </Block>
                    <Block widthPct={50}>
                        <NumberInput
                            isRequiredLabel={isRequiredLabel}
                            value={commodity.width ?? undefined}
                            error={commodityErrors.width}
                            label="Wd"
                            placeholder="Wd"
                            suffix={` ${measurementType}`}
                            onChange={(width) =>
                                onChange({ ...commodity, width })
                            }
                        />
                    </Block>
                </Row>
                <Row gap="p1">
                    <Block widthPct={50}>
                        <NumberInput
                            isRequiredLabel={isRequiredLabel}
                            value={commodity.height ?? undefined}
                            error={commodityErrors.height}
                            label="Ht"
                            placeholder="Ht"
                            suffix={` ${measurementType}`}
                            onChange={(height) =>
                                onChange({ ...commodity, height })
                            }
                        />
                    </Block>
                    <Block widthPct={50}>
                        <NumberInput
                            isRequiredLabel={isRequiredLabel}
                            value={commodity.weight ?? undefined}
                            error={commodityErrors.weight}
                            label="Total Wt"
                            placeholder="Total Wt"
                            suffix={` ${weightType}`}
                            onChange={(weight) =>
                                onChange({ ...commodity, weight })
                            }
                        />
                    </Block>
                </Row>
            </StyledConfirmShipmentDetailsWidgetCommodity>
        )
    }

    return (
        <StyledConfirmShipmentDetailsWidgetCommodity gap="p2">
            <Row justifyContent="space-between">
                <Typography isBold variant="body2" color="primary">
                    {label}
                </Typography>
                <Row gap="sm">
                    <IconButton size="sm" onClick={onDuplicate}>
                        <Copy size={16} />
                    </IconButton>
                    {!hideDeleteButton && (
                        <IconButton size="sm" onClick={onRemove}>
                            <X size={16} />
                        </IconButton>
                    )}
                </Row>
            </Row>
            <Row gap="p1">
                <div style={{ width: '80%' }}>
                    <TextInput
                        isRequiredLabel={isRequiredLabel}
                        label="Description"
                        placeholder="Description"
                        value={commodity.description ?? ''}
                        error={commodityErrors.description}
                        onChange={(description) =>
                            onChange({ ...commodity, description })
                        }
                    />
                </div>
                {/* "# of units" field temporarily hidden - may be added back later
                <div style={{ width: '20%' }}>
                    <NumberInput
                        label="# of units"
                        placeholder="# of units"
                        value={commodity.numOfUnits ?? undefined}
                        error={commodityErrors.numOfUnits}
                        onChange={(numOfUnits) =>
                            onChange({ ...commodity, numOfUnits })
                        }
                    />
                </div>
                */}
            </Row>
            <Row gap="p1">
                <Block widthPct={26}>
                    <Select
                        shouldPortalMenu
                        label="Type"
                        value={commodity.freightType ?? undefined}
                        placeholder="Type"
                        menuPlacement="top"
                        options={FREIGHT_TYPE_OPTIONS}
                        onSelectedOptionChanged={(freightType) =>
                            onChange({
                                ...commodity,
                                freightType:
                                    freightType as ConfirmShipmentDetailsCommodityItem['freightType'],
                            })
                        }
                    />
                </Block>
                <Block widthPct={14}>
                    <NumberInput
                        isRequiredLabel={isRequiredLabel}
                        label="Qty"
                        placeholder="Qty"
                        value={commodity.quantity ?? undefined}
                        error={commodityErrors.quantity}
                        onChange={(quantity) =>
                            onChange({ ...commodity, quantity })
                        }
                    />
                </Block>
                <Block widthPct={14}>
                    <NumberInput
                        isRequiredLabel={isRequiredLabel}
                        label="Lg"
                        value={commodity.length ?? undefined}
                        error={commodityErrors.len}
                        placeholder="Lg"
                        suffix={` ${measurementType}`}
                        onChange={(length) =>
                            onChange({ ...commodity, length })
                        }
                    />
                </Block>
                <Block widthPct={14}>
                    <NumberInput
                        isRequiredLabel={isRequiredLabel}
                        value={commodity.width ?? undefined}
                        error={commodityErrors.width}
                        label="Wd"
                        placeholder="Wd"
                        suffix={` ${measurementType}`}
                        onChange={(width) => onChange({ ...commodity, width })}
                    />
                </Block>
                <Block widthPct={14}>
                    <NumberInput
                        isRequiredLabel={isRequiredLabel}
                        value={commodity.height ?? undefined}
                        error={commodityErrors.height}
                        label="Ht"
                        placeholder="Ht"
                        suffix={` ${measurementType}`}
                        onChange={(height) =>
                            onChange({ ...commodity, height })
                        }
                    />
                </Block>
                <Block widthPct={16}>
                    <NumberInput
                        isRequiredLabel={isRequiredLabel}
                        value={commodity.weight ?? undefined}
                        error={commodityErrors.weight}
                        label="Total Wt"
                        placeholder="Total Wt"
                        suffix={` ${weightType}`}
                        onChange={(weight) =>
                            onChange({ ...commodity, weight })
                        }
                    />
                </Block>
            </Row>
        </StyledConfirmShipmentDetailsWidgetCommodity>
    )
}
